import { ActivatedRoute } from '@angular/router';
import { ActivityItem, ActivityProductImage, ActivitySummary, Archive, Archiving, PaymentSearchResult } from './activity.models';
import { Navigate } from '@ngxs/router-plugin';
import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  OrganizationState,
  ActivityActions,
  ActivityStateModel,
  ToastActions,
  Activity,
  ActivityProduct,
  ActivityListItem,
  ParticipantState,
  ParticipantActions,
  AccountActions,
  ServerEventsActions,
  RegistrationState,
  RegistrationActions,
  Registration,
  EmailType,
  DialogActions,
} from '@app/_store';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { of, Observable, throwError } from 'rxjs';
import { ActivityProductService, ActivityService } from '@app/_services';
import { patch, removeItem, append, updateItem } from '@ngxs/store/operators';
import { UiServices } from '@app/_helpers';

const defaults = {
  currentActivity: undefined,
  templates: undefined,
  createError: false,
  activities: undefined,
  archives: undefined,
  paymentSearchResult: undefined,
  summary: {
    total: 0,
  },
  isExportingOrImporting: false,
} as ActivityStateModel;

@State<ActivityStateModel>({
  name: 'activity',
  defaults,
  children: [ParticipantState, RegistrationState],
})
@Injectable()
export class ActivityState {
  constructor(
    private activityService: ActivityService,
    private activityProductService: ActivityProductService,
    private store: Store,
    private route: ActivatedRoute,
    private uiServices: UiServices,
    private actions$: Actions
  ) {}

  @Selector()
  static currentActivity(state: ActivityStateModel): Activity {
    return state.currentActivity;
  }

  @Selector()
  static currentActivities(state: ActivityStateModel): ActivityListItem[] {
    return state.activities;
  }

  @Selector()
  static templates(state: ActivityStateModel): ActivityItem[] {
    return state.templates;
  }

  @Selector()
  static archives(state: ActivityStateModel): Archive[] {
    return state.archives;
  }

  @Selector()
  static searchPaymentResult(state: ActivityStateModel): PaymentSearchResult[] {
    return state.paymentSearchResult;
  }

  @Selector()
  static isExportingOrImporting(state: ActivityStateModel): boolean {
    return state.isExportingOrImporting;
  }

  @Action(ActivityActions.Create)
  create({ dispatch, patchState }: StateContext<ActivityStateModel>, action: ActivityActions.Create): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;

    let templateId = '';
    if (typeof action.template === 'string') {
      templateId = action.template;
    } else {
      templateId = action.template?.id || '';
    }

    return this.activityService.create(action.form, action.type || '', templateId).pipe(
      concatMap(activityResponse => {
        return dispatch(new ToastActions.ShowSuccess(`Aktiviteten ${activityResponse.name} har skapats`)).pipe(
          map(() => activityResponse)
        );
      }),
      concatMap(activityResponse => dispatch(new ActivityActions.Init()).pipe(map(() => activityResponse))),
      concatMap(activityResponse => {
        return dispatch(new ActivityActions.View(activityResponse.id)).pipe(map(() => activityResponse));
      }),
      concatMap(activityResponse =>
        dispatch(new Navigate(['organization', organizationId, 'activities', activityResponse.id], { isNew: 1 })).pipe(
          map(() => activityResponse)
        )
      ),
      switchMap(activityResponse => {
        if (!activityResponse.templateMessage || activityResponse.templateMessage.length === 0) return of(null);

        return this.store.dispatch(
          new DialogActions.Confirm(
            `Instruktioner från ${activityResponse.templateResponsible}`,
            activityResponse.templateMessage.replaceAll('\n', '<br/>'),
            () => of(null),
            false,
            'Okej!',
            false
          )
        );
      }),
      catchError(() => {
        patchState({
          createError: true,
        });
        return of(null);
      })
    );
  }

  @Action(ActivityActions.SearchPayment)
  searchPayment(ctx: StateContext<ActivityStateModel>, action: ActivityActions.SearchPayment) {
    return this.activityService.searchPayment(action.reference).pipe(
      map(response => {
        ctx.setState(
          patch<ActivityStateModel>({
            paymentSearchResult: response.result,
          })
        );
      })
    );
  }

  @Action(ActivityActions.ClearSearchPaymentResults)
  clearSearchPaymentResults(ctx: StateContext<ActivityStateModel>) {
    ctx.setState(
      patch<ActivityStateModel>({
        paymentSearchResult: undefined,
      })
    );
  }

  @Action(ActivityActions.Update)
  update(ctx: StateContext<ActivityStateModel>, action: ActivityActions.Update): Observable<void> {
    const current = ctx.getState().currentActivity.id;
    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'name',
          value: action.form.name,
        },
        {
          op: 'replace',
          path: 'description',
          value: action.form.description,
        },
        {
          op: 'replace',
          path: 'deliveryInformation',
          value: action.form.deliveryInformation,
        },
        {
          op: 'replace',
          path: 'responsible/name',
          value: action.form.responsible.name,
        },
        {
          op: 'replace',
          path: 'responsible/email',
          value: action.form.responsible.email,
        },
        {
          op: 'replace',
          path: 'settings/partialPaymentAllowed',
          value: action.form.partialPaymentAllowed,
        },
        {
          op: 'replace',
          path: 'settings/smsPaymentReminderEnabled',
          value: action.form.smsPaymentReminderEnabled,
        },
        {
          op: 'replace',
          path: 'settings/subParticipantsAllowed',
          value: action.form.subParticipantsAllowed,
        },
        {
          op: 'replace',
          path: 'settings/subParticipantSwishOption',
          value: action.form.subParticipantSwishOption,
        },
        {
          op: 'replace',
          path: 'templateMessage',
          value: action.form.templateMessage,
        },
        {
          op: 'replace',
          path: 'settings/swish',
          value: action.form.swish,
        },
      ])
      .pipe(
        tap(() => {
          return ctx.dispatch(new ToastActions.ShowSuccess(`${action.form.name} har uppdaterats`));
        }),
        tap(() => {
          ctx.setState(
            patch<ActivityStateModel>({
              currentActivity: patch<Activity>({
                name: action.form.name,
                templateMessage: action.form.templateMessage,
                description: action.form.description,
                deliveryInformation: action.form.deliveryInformation,
                responsible: {
                  name: action.form.responsible.name,
                  email: action.form.responsible.email,
                },
                settings: {
                  partialPaymentAllowed: action.form.partialPaymentAllowed,
                  smsPaymentReminderEnabled: action.form.smsPaymentReminderEnabled,
                  subParticipantsAllowed: action.form.subParticipantsAllowed,
                  subParticipantSwishOption: action.form.subParticipantSwishOption,
                  swish: action.form.swish,
                },
              }),
              activities: updateItem<ActivityListItem>(
                a => a.id == current,
                patch<ActivityListItem>({
                  name: action.form.name,
                  responsible: action.form.responsible.name,
                })
              ),
            })
          );
        }),
        catchError(() => {
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när aktiviteten skulle sparas.'));
        })
      );
  }

  @Action(ActivityActions.ClearCurrent)
  clearCurrent(ctx: StateContext<ActivityStateModel>): Observable<unknown> {
    const current = ctx.getState().currentActivity;
    if (current) {
      ctx.patchState({
        currentActivity: undefined,
      });

      return ctx
        .dispatch(new ServerEventsActions.UnsubscribeFromActivityEvents(current.id))
        .pipe(concatMap(() => ctx.dispatch(new ParticipantActions.Reset())));
    }
    return of(null);
  }

  @Action(ActivityActions.Reset)
  reset(ctx: StateContext<ActivityStateModel>): Observable<void> {
    ctx.setState(defaults);
    ctx.dispatch(new RegistrationActions.Reset());
    return of(undefined);
  }

  @Action(ActivityActions.UpdateRequired)
  updateRequired(ctx: StateContext<ActivityStateModel>, action: ActivityActions.UpdateRequired) {
    const currentActivity = ctx.getState().currentActivity;
    if (currentActivity && currentActivity.id == action.activityId) {
      ctx.setState(
        patch<ActivityStateModel>({
          currentActivity: patch<Activity>({
            updateRequired: true,
          }),
        })
      );
    }
  }

  @Action(ActivityActions.View)
  view(ctx: StateContext<ActivityStateModel>, action: ActivityActions.View): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    if (!currentOrganization) return of(null);

    return ctx.dispatch(new AccountActions.Initialize(currentOrganization.id, action.id)).pipe(
      concatMap(() => {
        return this.activityService.get(action.id).pipe(
          tap(activity => {
            ctx.patchState({
              currentActivity: {
                ...activity,
                productSelectionDeadline: activity.productSelectionDeadline
                  ? new Date(activity.productSelectionDeadline)
                  : undefined,
                productSelectionDeadlineSuggestion: activity.productSelectionDeadlineSuggestion
                  ? new Date(activity.productSelectionDeadlineSuggestion)
                  : undefined,
                productSelectionReminders: activity.productSelectionReminders.map(r => new Date(r)),
                paymentDeadline: activity.paymentDeadline ? new Date(activity.paymentDeadline) : undefined,
                paymentDeadlineSuggestion: activity.paymentDeadlineSuggestion
                  ? new Date(activity.paymentDeadlineSuggestion)
                  : undefined,
                paymentReminders: activity.paymentReminders.map(r => new Date(r)),
                haveUserSelectableProducts: activity.products.filter(p => p.isUserSelectable).length > 0,
                summary: undefined,
                registration: activity.registration
                  ? {
                      ...activity.registration,
                      registrationDeadline: activity.registration.registrationDeadline
                        ? new Date(activity.registration.registrationDeadline)
                        : undefined,
                    }
                  : undefined,
              },
            });
          }),
          tap(() => ctx.dispatch(new RegistrationActions.Initialize())),
          concatMap(() => ctx.dispatch(new ServerEventsActions.Connect())),
          concatMap(() => ctx.dispatch(new ServerEventsActions.SubscribeToActivityEvents(action.id))),
          switchMap(() => ctx.dispatch(new ParticipantActions.Load())),
          switchMap(() => ctx.dispatch(new ParticipantActions.LoadNotifications(EmailType.ParticipantActivityLink)))
        );
      })
    );
  }

  @Action(ActivityActions.Init)
  loadActivities(ctx: StateContext<ActivityStateModel>): Observable<ActivityListItem[]> {
    return this.activityService.getAll().pipe(
      map(response => {
        ctx.patchState({
          activities: response.activities.map(activity => {
            return {
              ...activity,
              productSelectionDeadline: activity.productSelectionDeadline
                ? new Date(activity.productSelectionDeadline)
                : undefined,
              paymentDeadline: activity.paymentDeadline ? new Date(activity.paymentDeadline) : undefined,
            };
          }),
        });
        return response.activities;
      })
    );
  }

  @Action(ActivityActions.LoadTemplates)
  loadTemplates(ctx: StateContext<ActivityStateModel>): Observable<ActivityItem[]> {
    return this.activityService.getTemplates().pipe(
      map(response => {
        ctx.patchState({
          templates: response,
        });
        return response;
      })
    );
  }

  @Action(ActivityActions.DeleteActivity)
  deleteActivity(ctx: StateContext<ActivityStateModel>): Observable<void> {
    const current = ctx.getState().currentActivity.id;
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;

    return this.activityService.deleteActivity().pipe(
      map(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            activities: removeItem<ActivityListItem>(a => a.id == current),
          })
        );
      }),
      tap(() => {
        return ctx.dispatch(new Navigate(['organization', organizationId]));
      }),
      tap(() => {
        return ctx.dispatch(new ActivityActions.ClearCurrent());
      })
    );
  }

  @Action(ActivityActions.LockProduct)
  lockProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.LockProduct): Observable<void> {
    ctx.setState(
      patch<ActivityStateModel>({
        currentActivity: patch({
          products: updateItem(
            x => x.id === action.productId,
            patch<ActivityProduct>({
              locked: true,
            })
          ),
        }),
      })
    );

    return of();
  }

  @Action(ActivityActions.CreateProduct)
  createProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.CreateProduct): Observable<ActivityProduct> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.createProduct(organizationId, activityId, action.product).pipe(
      map(product => {
        const state = ctx.getState();
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch<Activity>({
              products: append([product]),
              haveUserSelectableProducts: state.currentActivity.haveUserSelectableProducts || product.isUserSelectable,
            }),
          })
        );
        return product;
      }),
      concatMap(product => {
        this.updateCurrentActivityCountsInList(ctx);
        return ctx.dispatch(new ToastActions.ShowSuccess(`${product.title} har lagts till!`)).pipe(map(() => product));
      })
    );
  }

  @Action(ActivityActions.CopyProductsFromActivity)
  copyProductsFromActivity(ctx: StateContext<ActivityStateModel>, action: ActivityActions.CopyProductsFromActivity) {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;

    return this.activityProductService
      .copyFromActivity(organizationId, activityId, action.fromActivityId, action.isArchived)
      .pipe(
        tap(() => {
          return ctx.dispatch(new ActivityActions.View(activityId));
        }),
        switchMap(x => {
          return ctx.dispatch(new ToastActions.ShowSuccess(`${x.productCount} produkter kopierades till aktiviteten.`));
        })
      );
  }

  @Action(ActivityActions.UpdateCurrentActivityCountsInList)
  public updateCurrentActivityCountsInList(ctx: StateContext<ActivityStateModel>) {
    const state = ctx.getState();
    const id = state.currentActivity.id;
    const productCount = state.currentActivity.products.length;
    const participantCont = this.store.selectSnapshot(ParticipantState.participants).length;
    if (state.activities)
      ctx.setState(
        patch<ActivityStateModel>({
          activities: updateItem<ActivityListItem>(
            a => a.id === id,
            patch({
              productCount: productCount,
              participantCount: participantCont,
            })
          ),
        })
      );
  }

  @Action(ActivityActions.DeleteProduct)
  deleteProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.DeleteProduct): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.deleteProduct(organizationId, activityId, action.product.id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch<Activity>({
              haveUserSelectableProducts:
                state.currentActivity.products.filter(p => p.id !== action.product.id && p.isUserSelectable).length > 0,
              products: removeItem<ActivityProduct>(p => p.id === action.product.id),
            }),
          })
        );
      }),
      switchMap(() => {
        this.updateCurrentActivityCountsInList(ctx);
        return ctx.dispatch(new ToastActions.ShowSuccess(`${action.product.title} har tagts bort!`));
      })
    );
  }

  @Action(ActivityActions.UnlockProduct)
  unlockProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.UnlockProduct): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.unlockProduct(organizationId, activityId, action.product.id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch<Activity>({
              haveUserSelectableProducts:
                state.currentActivity.products.filter(p => p.id !== action.product.id && p.isUserSelectable).length > 0,
              products: updateItem<ActivityProduct>(
                p => p.id === action.product.id,
                patch<ActivityProduct>({
                  locked: false,
                })
              ),
            }),
          })
        );
      }),
      switchMap(() => {
        return ctx.dispatch(new ToastActions.ShowSuccess(`${action.product.title} har låsts upp!`));
      })
    );
  }

  @Action(ActivityActions.AddProductImage)
  addProductImage(ctx: StateContext<ActivityStateModel>, action: ActivityActions.AddProductImage): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.addProductImage(organizationId, activityId, action.productId, action.file).pipe(
      map((productImage: ActivityProductImage) => {
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch({
              products: updateItem(
                x => x.id === action.productId,
                patch<ActivityProduct>({
                  images: append([productImage]),
                })
              ),
            }),
          })
        );
      })
    );
  }

  @Action(ActivityActions.DeleteProductImage)
  deleteProductImage(ctx: StateContext<ActivityStateModel>, action: ActivityActions.DeleteProductImage): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.deleteProductImage(organizationId, activityId, action.productId, action.imageId).pipe(
      map(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch({
              products: updateItem(
                x => x.id === action.productId,
                patch<ActivityProduct>({
                  images: removeItem(x => x.id == action.imageId),
                })
              ),
            }),
          })
        );
      })
    );
  }

  @Action(ActivityActions.SetActivityImage)
  setActivityImage(ctx: StateContext<ActivityStateModel>, action: ActivityActions.SetActivityImage): Observable<void> {
    const current = ctx.getState().currentActivity.id;
    return this.activityService.setActivityImage(action.file).pipe(
      map(response => {
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch({
              imageUrl: response.url,
              imageSize: {
                width: response.width,
                height: response.height,
              },
            }),
            activities: updateItem<ActivityListItem>(
              a => a.id == current,
              patch({
                imageUrl: response.url,
              })
            ),
          })
        );
      }),
      catchError(() => {
        ctx.dispatch(
          new ToastActions.ShowError(`Ett fel uppstod när bilden skulle sparas. Bilden måste vara minst 400 pixlar bred och hög`)
        );
        return of(null);
      })
    );
  }

  @Action(ActivityActions.DeleteActivityImage)
  deleteActivityImage(ctx: StateContext<ActivityStateModel>): Observable<void> {
    const current = ctx.getState().currentActivity.id;

    return this.activityService.deleteActivityImage().pipe(
      map(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch({
              imageUrl: undefined,
            }),
            activities: updateItem<ActivityListItem>(
              a => a.id == current,
              patch({
                imageUrl: undefined,
              })
            ),
          })
        );
      })
    );
  }

  @Action(ActivityActions.ArchiveActivity)
  archiveActivity(ctx: StateContext<ActivityStateModel>): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;

    const current = ctx.getState().currentActivity.id;
    return this.activityService.archiveActivity().pipe(
      map(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            activities: removeItem<ActivityListItem>(a => a.id == current),
          })
        );
      }),
      tap(() => {
        return ctx.dispatch(new Navigate(['organization', organizationId]));
      }),
      tap(() => {
        return ctx.dispatch(new ActivityActions.ClearCurrent());
      })
    );
  }

  @Action(ActivityActions.UpdateProduct)
  updateProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.UpdateProduct): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService
      .updateProduct(organizationId, activityId, action.productId, action.product, action.updateParticipants)
      .pipe(
        map(product => {
          const state = ctx.getState();
          ctx.setState(
            patch<ActivityStateModel>({
              currentActivity: patch<Activity>({
                haveUserSelectableProducts:
                  state.currentActivity.products.filter(p => p.id !== product.id && p.isUserSelectable).length > 0 ||
                  product.isUserSelectable,
                products: updateItem(p => p.id === action.productId, product),
              }),
            })
          );
        }),
        tap(() => {
          if (action.updateParticipants) this.store.dispatch(new ParticipantActions.Load());
        }),
        tap(() => ctx.dispatch(new ToastActions.ShowSuccess(`${action.product.title} har uppdaterats!`)))
      );
  }

  @Action(ActivityActions.ChangeProductOrder)
  changeProductOrder(ctx: StateContext<ActivityStateModel>, action: ActivityActions.ChangeProductOrder): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    return this.activityProductService.changeProductOrder(organizationId, activityId, action.productId, action.direction).pipe(
      map(response => {
        const order = response.productOrder;

        const products = ctx.getState().currentActivity.products;

        const newOrder = [...products].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));

        ctx.setState(
          patch<ActivityStateModel>({
            currentActivity: patch<Activity>({
              products: newOrder,
            }),
          })
        );
      })
    );
  }

  // Drag and drop: the list is reordered right away so the row stays where it was dropped, then the API's order is applied.
  // If the call fails the previous order is put back (the error itself is shown by the global error handling).
  @Action(ActivityActions.MoveProduct)
  moveProduct(ctx: StateContext<ActivityStateModel>, action: ActivityActions.MoveProduct): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;
    const activityId = ctx.getState().currentActivity.id;
    const previousOrder = ctx.getState().currentActivity.products;

    // Not CDK's moveItemInArray: this state is in the initial bundle and the drag-drop package should stay in the activity chunk
    const moved = previousOrder.filter(p => p.id !== action.productId);
    moved.splice(
      action.position,
      0,
      previousOrder.find(p => p.id === action.productId)
    );
    ctx.setState(patch<ActivityStateModel>({ currentActivity: patch<Activity>({ products: moved }) }));

    return this.activityProductService.moveProduct(organizationId, activityId, action.productId, action.position).pipe(
      map(response => {
        const order = response.productOrder;
        const products = ctx.getState().currentActivity.products;
        const newOrder = [...products].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));

        ctx.setState(patch<ActivityStateModel>({ currentActivity: patch<Activity>({ products: newOrder }) }));
      }),
      catchError(err => {
        ctx.setState(patch<ActivityStateModel>({ currentActivity: patch<Activity>({ products: previousOrder }) }));
        return throwError(() => err);
      })
    );
  }

  getSummary(): ActivitySummary {
    let total = 0;
    let payments = 0;
    let balance = 0;

    const participants = this.store.selectSnapshot(ParticipantState.participants);

    if (participants) {
      participants.forEach(participant => {
        if (!participant.swishRecipient || participant.swishRecipient.length === 0) {
          participant.products.forEach(product => {
            total += product.amount * product.quantity;
          });
          participant.payments.forEach(payment => {
            payments += payment.amount;
          });
        }
      });

      balance = total > payments ? -(total - payments) : payments - total;

      return {
        total,
        payments,
        balance,
      };
    }

    return {
      total: 0,
      payments: 0,
      balance: 0,
    };
  }

  @Action(ActivityActions.CalculateSummary)
  calculateSummary(ctx: StateContext<ActivityStateModel>): Observable<void> {
    const state = ctx.getState();
    ctx.patchState({
      currentActivity: {
        ...state.currentActivity,
        summary: this.getSummary(),
      },
    });
    return of(null);
  }

  @Action(ActivityActions.ActivateProductSelections)
  activateProductSelections(
    ctx: StateContext<ActivityStateModel>,
    action: ActivityActions.ActivateProductSelections
  ): Observable<void> {
    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'productSelectionDeadline',
          value: action.deadline ? action.deadline.toISOString() : null,
        },
        {
          op: 'replace',
          path: 'productSelectionReminderEnabled',
          value: action.productSelectionReminderEnabled,
        },
      ])
      .pipe(
        map(() => {
          const currentActivity = ctx.getState().currentActivity;
          const deadline = action.deadline ? new Date(action.deadline) : action.deadline;
          ctx.setState(
            patch<ActivityStateModel>({
              currentActivity: patch<Activity>({
                productSelectionDeadline: deadline,
                productSelectionReminderEnabled: action.productSelectionReminderEnabled,
              }),
              activities: updateItem<ActivityListItem>(
                i => i.id == currentActivity.id,
                patch<ActivityListItem>({
                  productSelectionDeadline: deadline,
                })
              ),
            })
          );
          return currentActivity.type === 'template';
        }),
        switchMap(isTemplate => {
          let text: string;

          if (isTemplate) {
            text = action.deadline
              ? 'Rekommenderat sista datum för produktval har sparats'
              : 'Rekommenderat sista datum för produktval har tagits bort';
          } else {
            text = action.deadline
              ? action.productSelectionReminderEnabled
                ? 'Deltagarna kan nu välja produkter/avgifter via sin aktivitetslänk och påminnelser kommer skickas automatiskt'
                : 'Deltagarna kan nu välja produkter/avgifter via sin aktivitetslänk'
              : 'Möjligheten för deltagarna att välja produkter/avgifter har stängts av';
          }

          return ctx.dispatch(new ToastActions.ShowSuccess(text));
        })
      );
  }

  @Action(ActivityActions.ToggleTemplatePublished)
  toggleTemplatePublished(ctx: StateContext<ActivityStateModel>) {
    const current = ctx.getState().currentActivity.publishedAsTemplate || false;

    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'publishedAsTemplate',
          value: !current,
        },
      ])
      .pipe(
        map(() => {
          const currentActivityId = ctx.getState().currentActivity.id;

          ctx.setState(
            patch<ActivityStateModel>({
              activities: updateItem<ActivityListItem>(
                i => i.id == currentActivityId,
                patch<ActivityListItem>({
                  publishedAsTemplate: !current,
                })
              ),
              currentActivity: patch<Activity>({
                publishedAsTemplate: !current,
              }),
            })
          );
        })
      );
  }

  @Action(ActivityActions.ActivatePayments)
  activatePayments(ctx: StateContext<ActivityStateModel>, action: ActivityActions.ActivatePayments): Observable<void> {
    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'paymentDeadline',
          value: action.deadline ? action.deadline.toISOString() : null,
        },
        {
          op: 'replace',
          path: 'paymentReminderEnabled',
          value: action.paymentReminderEnabled,
        },
      ])
      .pipe(
        map(() => {
          let nextArchivingDate: Date = undefined;
          if (action.deadline) {
            nextArchivingDate = new Date(action.deadline);
            nextArchivingDate.setDate(nextArchivingDate.getDate() + 21);
          }

          const currentActivity = ctx.getState().currentActivity;
          const deadline = action.deadline ? new Date(action.deadline) : undefined;

          ctx.setState(
            patch<ActivityStateModel>({
              currentActivity: patch<Activity>({
                paymentDeadline: deadline,
                archiving: patch<Archiving>({
                  nextCheck: action.deadline ? nextArchivingDate : undefined,
                }),
                paymentReminderEnabled: action.paymentReminderEnabled,
              }),
              activities: updateItem<ActivityListItem>(
                i => i.id == currentActivity.id,
                patch<ActivityListItem>({
                  paymentDeadline: deadline,
                })
              ),
            })
          );
          return currentActivity.type === 'template';
        }),
        switchMap(isTemplate => {
          let text: string;

          if (isTemplate) {
            text = action.deadline
              ? 'Rekommenderat sista datum för betalning har sparats'
              : 'Rekommenderat sista datum för betalning  har tagits bort';
          } else {
            text = action.deadline
              ? action.paymentReminderEnabled
                ? 'Deltagarna i aktiviteten kan nu betala och påminnelser kommer skickas automatiskt'
                : 'Deltagarna i aktiviteten kan nu betala'
              : 'Möjligheten för deltagarna att betala har stängts av';
          }

          return ctx.dispatch(new ToastActions.ShowSuccess(text));
        })
      );
  }

  @Action(ActivityActions.Export)
  export(ctx: StateContext<ActivityStateModel>) {
    ctx.setState(
      patch<ActivityStateModel>({
        isExportingOrImporting: true,
      })
    );
    const fileName = `${ctx.getState().currentActivity.name}.zip`;

    return this.activityService.exportActivity().pipe(
      tap(zipFile => this.uiServices.downloadFile(zipFile, fileName, 'application/octet-stream')),
      tap(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            isExportingOrImporting: false,
          })
        );
      })
    );
  }

  @Action(ActivityActions.Import)
  import(ctx: StateContext<ActivityStateModel>, action: ActivityActions.Import) {
    ctx.setState(
      patch<ActivityStateModel>({
        isExportingOrImporting: true,
      })
    );
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.activityService.importActivity(action.data).pipe(
      map(response => {
        ctx.dispatch(new ActivityActions.Init());
        return response;
      }),
      map(response => {
        ctx.dispatch(new Navigate(['organization', currentOrganization.id, 'activities', response.id]));
      }),
      tap(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            isExportingOrImporting: false,
          })
        );
      }),
      catchError(_ => {
        ctx.dispatch(new ToastActions.ShowError('Det uppstod ett fel vid importen'));
        throw _;
      })
    );
  }

  @Action(ActivityActions.ActivateRegistration)
  activateRegistration(ctx: StateContext<ActivityStateModel>, action: ActivityActions.ActivateRegistration): Observable<void> {
    return this.activityService
      .patchActivity([
        {
          op: 'replace',
          path: 'registration/registrationDeadline',
          value: action.deadline ? action.deadline.toISOString() : null,
        },
      ])
      .pipe(
        map(() => {
          ctx.setState(
            patch<ActivityStateModel>({
              currentActivity: patch<Activity>({
                registration: patch<Registration>({
                  registrationDeadline: action.deadline ? new Date(action.deadline) : undefined,
                }),
              }),
            })
          );
        }),
        tap(() => {
          ctx.dispatch(new RegistrationActions.Initialize());
        }),
        tap(() =>
          ctx.dispatch(
            new ToastActions.ShowSuccess(
              action.deadline ? 'Anmälan är nu öppen för allmänheten' : 'Möjligheten att anmäla sig har stängts av'
            )
          )
        )
      );
  }

  @Action(ActivityActions.DownloadActivityReport)
  downloadReport(ctx: StateContext<ActivityStateModel>, action: ActivityActions.DownloadActivityReport) {
    ctx.dispatch(new ToastActions.ShowSuccess('Skapar rapport...'));
    return this.activityService
      .downloadReport(action.report)
      .pipe(tap(report => this.uiServices.downloadFile(report, action.fileName, 'application/pdf')));
  }

  @Action(ActivityActions.LoadArchives)
  loadArchives(ctx: StateContext<ActivityStateModel>) {
    const scrollToId = this.route.snapshot.queryParamMap.get('id');

    return this.activityService.getArchives().pipe(
      map(archiveList => {
        ctx.patchState({
          archives: archiveList.archives.map(m => {
            return {
              ...m,
              expanded: scrollToId && scrollToId == m.id ? true : false,
            };
          }),
        });
      })
    );
  }

  @Action(ActivityActions.DownloadArchive)
  downloadArchive(ctx: StateContext<ActivityStateModel>, action: ActivityActions.DownloadArchive) {
    ctx.dispatch(new ToastActions.ShowInformation(`Påbörjar nerladdning av ${action.archive.archiveName}...`));

    return this.activityService
      .downloadArchive(action.archive.id)
      .pipe(
        tap(archiveZip =>
          this.uiServices.downloadFile(archiveZip, `${action.archive.archiveName}.zip`, 'application/octet-stream')
        )
      );
  }

  @Action(ActivityActions.DeleteArchive)
  deleteArchive(ctx: StateContext<ActivityStateModel>, action: ActivityActions.DeleteArchive) {
    ctx.dispatch(new ToastActions.ShowInformation(`Påbörjar borttagning av ${action.archive.archiveName}...`));

    return this.activityService.deleteArchive(action.archive.id).pipe(
      tap(() => {
        ctx.setState(
          patch<ActivityStateModel>({
            archives: removeItem(x => x.id == action.archive.id),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(new ToastActions.ShowSuccess(`${action.archive.archiveName} har tagits bort från arkivet!`));
      })
    );
  }

  @Action(ActivityActions.ResetArchives)
  resetArchives(ctx: StateContext<ActivityStateModel>) {
    ctx.patchState({
      archives: undefined,
    });
  }
}
